import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';

interface Game {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  path: string;
  color: 'green' | 'blue';
  icon: string;
}

interface GameCardProps {
  game: Game;
  playNowLabel: string;
}

export function GameCard({ game, playNowLabel }: GameCardProps) {
  const colorClasses = {
    green: {
      bg: 'bg-ukonnx-green/10 hover:bg-ukonnx-green/20',
      border: 'border-ukonnx-green/30 hover:border-ukonnx-green',
      icon: 'bg-ukonnx-green/20 text-ukonnx-green',
      button: 'bg-ukonnx-green text-bg-deep hover:bg-ukonnx-green-dark',
    },
    blue: {
      bg: 'bg-blue-500/10 hover:bg-blue-500/20',
      border: 'border-blue-500/30 hover:border-blue-500',
      icon: 'bg-blue-500/20 text-blue-400',
      button: 'bg-blue-500 text-white hover:bg-blue-600',
    },
  };

  const colors = colorClasses[game.color];

  return (
    <Link
      to={game.path}
      className={cn(
        'card p-4 sm:p-6 transition-all duration-300 group cursor-pointer',
        'hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98]',
        colors.bg,
        colors.border
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={cn(
          'w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-2xl sm:text-3xl shrink-0',
          'group-hover:scale-110 transition-transform',
          colors.icon
        )}>
          {game.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-1 truncate">
            {game.title}
          </h3>
          <p className="text-xs sm:text-sm text-text-secondary">
            {game.subtitle}
          </p>
        </div>
      </div>

      <p className="text-text-secondary mt-3 sm:mt-4 mb-4 sm:mb-6 text-xs sm:text-sm leading-relaxed line-clamp-3">
        {game.description}
      </p>

      <div className={cn(
        'inline-flex items-center gap-2 px-4 py-2.5 sm:py-2 rounded-lg font-semibold text-sm',
        'transition-all group-hover:gap-3 min-h-[44px]',
        colors.button
      )}>
        {playNowLabel}
        <span className="transition-transform group-hover:translate-x-1">→</span>
      </div>
    </Link>
  );
}
